import { jsPDF } from 'jspdf';
import type { RealTimeTranscriptLine, TranscriptHighlight } from '../types';
import buildTextSegments from './buildTextSegments';
import { formatTimestamp, formatUtcDateTime, splitSpeaker } from './formatters';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN_LEFT = 15;
const MARGIN_RIGHT = 15;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 15;
const LINE_NUM_COL_X = MARGIN_LEFT;
const TIMESTAMP_COL_X = 23;
const TEXT_COL_X = 62;
const TEXT_MAX_WIDTH = PAGE_WIDTH - TEXT_COL_X - MARGIN_RIGHT;
const BODY_FONT_SIZE = 9;
const TITLE_FONT_SIZE = 12;
const LINE_HEIGHT = 4.5;
const DIVIDER_FONT_SIZE = 8;
const DIVIDER_SPACING = 6;

const hasLineIdentity = (line: Partial<RealTimeTranscriptLine>) =>
  line.lineId != null || line.lineNumber != null || line.pageLineNumber != null;

type ExportPdfOptions = {
  showTimestamps?: boolean;
};

export default function exportTranscriptPdf(
  displayLines: RealTimeTranscriptLine[],
  highlights: TranscriptHighlight[],
  title?: string,
  options?: ExportPdfOptions,
): void {
  const showTimestamps = options?.showTimestamps !== false;
  const effectiveTextColX = showTimestamps ? TEXT_COL_X : TIMESTAMP_COL_X;
  const effectiveTextMaxWidth = PAGE_WIDTH - effectiveTextColX - MARGIN_RIGHT;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let cursorY = MARGIN_TOP;

  const pageBottom = PAGE_HEIGHT - MARGIN_BOTTOM;

  const ensureSpace = (needed: number) => {
    if (cursorY + needed > pageBottom) {
      doc.addPage();
      cursorY = MARGIN_TOP;
    }
  };

  const renderPageDivider = (pageNumber: number) => {
    const totalNeeded = DIVIDER_SPACING * 2 + 4 + LINE_HEIGHT;
    ensureSpace(totalNeeded);

    cursorY += DIVIDER_SPACING;

    // Horizontal rule
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(MARGIN_LEFT, cursorY, PAGE_WIDTH - MARGIN_RIGHT, cursorY);

    // Centered label
    const label = `PAGE ${pageNumber}`;
    doc.setFontSize(DIVIDER_FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 140, 140);
    const labelWidth = doc.getTextWidth(label);
    const centerX = (PAGE_WIDTH - labelWidth) / 2;

    // White background behind label
    doc.setFillColor(255, 255, 255);
    doc.rect(centerX - 2, cursorY - 3, labelWidth + 4, 5, 'F');

    doc.text(label, centerX, cursorY + 1);

    cursorY += DIVIDER_SPACING + 4;

    // Reset
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(BODY_FONT_SIZE);
  };

  // --- Header ---
  const headerText = title || 'Real-time transcript';
  doc.setFontSize(TITLE_FONT_SIZE);
  doc.setFont('helvetica', 'bold');
  doc.text(headerText, MARGIN_LEFT, cursorY);
  cursorY += 6;

  doc.setFontSize(BODY_FONT_SIZE);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(102, 102, 102);
  doc.text(`Exported: ${new Date().toLocaleString()}`, MARGIN_LEFT, cursorY);
  doc.setTextColor(0, 0, 0);
  cursorY += 8;

  // --- Build highlight lookup by lineId (supports multi-line highlights) ---
  const highlightsByLine = new Map<string, TranscriptHighlight[]>();
  for (const hl of highlights) {
    const lineSelections =
      hl.selections && hl.selections.length > 0
        ? hl.selections
        : [
            {
              lineId: hl.lineId,
              startOffset: hl.startOffset,
              endOffset: hl.endOffset,
              text: hl.text,
              lineNumberAtCreate: hl.lineNumberAtCreate,
            },
          ];

    for (const sel of lineSelections) {
      const existing = highlightsByLine.get(sel.lineId);
      const perLineView = {
        ...hl,
        lineId: sel.lineId,
        startOffset: sel.startOffset,
        endOffset: sel.endOffset,
        text: sel.text,
      };
      if (existing) {
        existing.push(perLineView);
      } else {
        highlightsByLine.set(sel.lineId, [perLineView]);
      }
    }
  }

  // --- Render lines ---
  let currentPage: number | undefined;

  for (const line of displayLines) {
    // Skip non-content lines
    if (!(typeof line.text === 'string' && line.text.length > 0) && !hasLineIdentity(line)) {
      continue;
    }

    // Detect implicit page change
    if (line.pageNumber != null && line.pageNumber !== currentPage) {
      currentPage = line.pageNumber;
      renderPageDivider(currentPage);
    }

    const timeLabel = line.finalizedAtUtc
      ? formatUtcDateTime(line.finalizedAtUtc)
      : formatTimestamp(line.timestamp);
    const { speaker, content } = splitSpeaker(line.text);
    const lineHighlights = highlightsByLine.get(line.id) || [];

    // Estimate height needed for this line
    doc.setFontSize(BODY_FONT_SIZE);
    doc.setFont('helvetica', 'normal');
    const wrappedContent = doc.splitTextToSize(line.text, effectiveTextMaxWidth) as string[];
    const contentHeight = wrappedContent.length * LINE_HEIGHT;
    const notesForLine = lineHighlights.filter(
      (h) => (h.note && h.note.trim()) || (h.comments && h.comments.length > 0),
    );
    const noteEntryCount = notesForLine.reduce(
      (sum, h) => sum + (h.note ? 1 : 0) + (h.comments?.length ?? 0),
      0,
    );
    const noteHeight = (notesForLine.length + noteEntryCount) * LINE_HEIGHT * 1.5;
    ensureSpace(contentHeight + noteHeight + 2);

    // Line number column
    const lineNum = line.pageLineNumber ?? line.lineNumber;
    if (lineNum != null) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(160, 160, 160);
      doc.setFontSize(BODY_FONT_SIZE);
      doc.text(String(lineNum), LINE_NUM_COL_X, cursorY);
    }

    // Timestamp column
    if (showTimestamps) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(92, 102, 112);
      doc.setFontSize(BODY_FONT_SIZE);
      doc.text(timeLabel, TIMESTAMP_COL_X, cursorY);
      doc.setTextColor(0, 0, 0);
    }

    // Text column - render with highlights
    if (lineHighlights.length > 0) {
      const segments = buildTextSegments(line.text, lineHighlights);
      let segX = effectiveTextColX;
      let segY = cursorY;

      for (const segment of segments) {
        if (!segment.text) continue;

        // Check if this segment contains the speaker prefix
        const segLines = doc.splitTextToSize(
          segment.text,
          effectiveTextMaxWidth - (segX - effectiveTextColX),
        ) as string[];

        for (let li = 0; li < segLines.length; li++) {
          const segLine = segLines[li];
          if (!segLine) continue;

          // Wrap to next line if needed
          if (li > 0) {
            segX = effectiveTextColX;
            segY += LINE_HEIGHT;
            ensureSpace(LINE_HEIGHT);
          }

          // Check remaining width
          doc.setFontSize(BODY_FONT_SIZE);
          const textWidth = doc.getTextWidth(segLine);

          if (segX + textWidth > PAGE_WIDTH - MARGIN_RIGHT) {
            segX = effectiveTextColX;
            segY += LINE_HEIGHT;
            ensureSpace(LINE_HEIGHT);
          }

          // Draw highlight background
          if (segment.highlightId) {
            doc.setFillColor(255, 217, 102);
            doc.rect(segX, segY - 3, textWidth + 0.5, LINE_HEIGHT, 'F');
          }

          // Determine if this part includes the speaker name
          if (speaker && segment === segments[0]) {
            const speakerPrefix = `${speaker}: `;
            if (segLine.startsWith(speakerPrefix)) {
              doc.setFont('helvetica', 'bold');
              doc.text(speakerPrefix, segX, segY);
              const speakerWidth = doc.getTextWidth(speakerPrefix);
              doc.setFont('helvetica', 'normal');
              const rest = segLine.slice(speakerPrefix.length);
              if (rest) {
                doc.text(rest, segX + speakerWidth, segY);
              }
              segX += textWidth;
              continue;
            }
          }

          doc.setFont('helvetica', 'normal');
          doc.text(segLine, segX, segY);
          segX += textWidth;
        }
      }
      cursorY = segY + LINE_HEIGHT;
    } else {
      // No highlights - simple render
      let textX = effectiveTextColX;
      if (speaker) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(BODY_FONT_SIZE);
        const speakerText = `${speaker}: `;
        doc.text(speakerText, textX, cursorY);
        const speakerWidth = doc.getTextWidth(speakerText);
        textX += speakerWidth;

        doc.setFont('helvetica', 'normal');
        const remaining = doc.splitTextToSize(
          content,
          effectiveTextMaxWidth - speakerWidth,
        ) as string[];
        if (remaining.length > 0) {
          doc.text(remaining[0], textX, cursorY);
          for (let i = 1; i < remaining.length; i++) {
            cursorY += LINE_HEIGHT;
            ensureSpace(LINE_HEIGHT);
            doc.text(remaining[i], effectiveTextColX, cursorY);
          }
        }
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(BODY_FONT_SIZE);
        const wrapped = doc.splitTextToSize(line.text, effectiveTextMaxWidth) as string[];
        for (let i = 0; i < wrapped.length; i++) {
          if (i > 0) {
            cursorY += LINE_HEIGHT;
            ensureSpace(LINE_HEIGHT);
          }
          doc.text(wrapped[i], textX, cursorY);
        }
      }
      cursorY += LINE_HEIGHT;
    }

    // Render notes with comments
    for (const hl of notesForLine) {
      const noteEntries: Array<{ author: string; text: string }> = [];
      if (hl.note && hl.note.trim()) {
        noteEntries.push({ author: hl.createdByName ?? '', text: hl.note });
      }
      if (hl.comments && hl.comments.length > 0) {
        for (const c of hl.comments) {
          noteEntries.push({ author: c.createdBy?.name ?? '', text: c.commentText });
        }
      }
      if (noteEntries.length === 0) continue;

      ensureSpace(LINE_HEIGHT * 1.5);
      doc.setTextColor(102, 102, 102);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(BODY_FONT_SIZE);
      doc.text('Notes:', TIMESTAMP_COL_X, cursorY);
      cursorY += LINE_HEIGHT;

      const noteContentX = TIMESTAMP_COL_X + 4;
      const noteMaxWidth = PAGE_WIDTH - MARGIN_RIGHT - noteContentX;

      for (let ei = 0; ei < noteEntries.length; ei++) {
        const entry = noteEntries[ei];
        if (ei > 0) cursorY += LINE_HEIGHT * 0.5;
        ensureSpace(LINE_HEIGHT * 2);

        // Author name in bold
        if (entry.author) {
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(102, 102, 102);
          doc.text(entry.author, noteContentX, cursorY);
          cursorY += LINE_HEIGHT;
        }

        // Comment text in italic
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(102, 102, 102);
        const wrapped = doc.splitTextToSize(`"${entry.text}"`, noteMaxWidth) as string[];
        for (let i = 0; i < wrapped.length; i++) {
          ensureSpace(LINE_HEIGHT);
          doc.text(wrapped[i], noteContentX, cursorY);
          cursorY += LINE_HEIGHT;
        }
      }
      doc.setTextColor(0, 0, 0);
    }

    // Small gap between lines
    cursorY += 1;
  }

  doc.save('transcript-export.pdf');
}
